import React from "react";
import {
  FiActivity,
  FiZap,
  FiDroplet,
  FiTarget,
  FiTrendingUp,
  FiCalendar,
  FiCheckCircle,
  FiPieChart,
} from "react-icons/fi";
import MetricCard from "./MetricCard";
import { FoodLogStats } from "./types";

interface NutritionAnalysisProps {
  readonly nutritionSummary: FoodLogStats["nutrition_summary"];
  readonly totalMeals: number;
  readonly daysAnalyzed: number;
  readonly daysWithMeals: number;
  readonly adherenceRate: number;
  readonly avgDailyCalories: number;
  readonly avgDailyMeals: number;
}

export default function NutritionAnalysis({
  nutritionSummary,
  totalMeals,
  daysAnalyzed,
  daysWithMeals,
  adherenceRate,
  avgDailyCalories,
  avgDailyMeals,
}: NutritionAnalysisProps) {
  if (!nutritionSummary || nutritionSummary.meals_with_taco_data === 0) {
    return null;
  }

  // Calcular calorias por macro
  const caloriasProteinas = nutritionSummary.total_proteina_g * 4;
  const caloriasCarbs = nutritionSummary.total_carboidrato_g * 4;
  const caloriasGorduras = nutritionSummary.total_lipideos_g * 9;
  const totalCaloriasMacros =
    caloriasProteinas + caloriasCarbs + caloriasGorduras;

  // Percentual de dados TACO disponíveis
  const tacoDataPercentage = (
    (nutritionSummary.meals_with_taco_data / totalMeals) *
    100
  ).toFixed(0);

  // Avaliar qualidade da distribuição de macros (baseado em recomendações gerais)
  const getNutritionQuality = () => {
    const { proteina_pct, carboidrato_pct, lipideos_pct } =
      nutritionSummary.distribuicao_macros;

    // Faixas recomendadas: Proteínas 10-35%, Carboidratos 45-65%, Gorduras 20-35%
    const proteinaOk = proteina_pct >= 10 && proteina_pct <= 35;
    const carboOk = carboidrato_pct >= 45 && carboidrato_pct <= 65;
    const gorduraOk = lipideos_pct >= 20 && lipideos_pct <= 35;

    if (proteinaOk && carboOk && gorduraOk) {
      return {
        status: "excelente",
        color: "text-green-600 dark:text-green-400",
        bgColor: "bg-green-100 dark:bg-green-900/30",
        message: "Distribuição equilibrada de macronutrientes",
      };
    } else if (proteinaOk || carboOk || gorduraOk) {
      return {
        status: "bom",
        color: "text-yellow-600 dark:text-yellow-400",
        bgColor: "bg-yellow-100 dark:bg-yellow-900/30",
        message: "Distribuição de macros pode ser ajustada",
      };
    } else {
      return {
        status: "atenção",
        color: "text-orange-600 dark:text-orange-400",
        bgColor: "bg-orange-100 dark:bg-orange-900/30",
        message: "Revisar distribuição de macronutrientes",
      };
    }
  };

  const nutritionQuality = getNutritionQuality();

  // Avaliar consumo de fibras
  const getFiberStatus = () => {
    if (nutritionSummary.avg_fibra_g >= 25) {
      return "✓ Consumo adequado de fibras (recomendado: 25-30g/dia)";
    } else if (nutritionSummary.avg_fibra_g >= 15) {
      return "Consumo moderado de fibras (recomendado: 25-30g/dia)";
    } else {
      return "⚠ Consumo baixo de fibras (recomendado: 25-30g/dia)";
    }
  };

  const fiberStatus = getFiberStatus();

  return (
    <>
      {/* Insights e Recomendações */}
      <div className="rounded-xl sm:rounded-2xl border border-stroke bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 sm:p-6 shadow-sm dark:border-strokedark">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-blue-100 dark:bg-blue-900/30 p-2">
            <FiPieChart className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h5 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2">
              Insights Nutricionais
            </h5>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-0.5">
                  •
                </span>
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  <strong>Cobertura de dados:</strong>{" "}
                  {nutritionSummary.meals_with_taco_data} de {totalMeals}{" "}
                  refeições ({tacoDataPercentage}%) possuem dados nutricionais
                  completos da tabela TACO
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-0.5">
                  •
                </span>
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  <strong>Distribuição calórica:</strong> Das{" "}
                  {totalCaloriasMacros.toFixed(0)} kcal totais dos macros,{" "}
                  {((caloriasProteinas / totalCaloriasMacros) * 100).toFixed(0)}
                  % são de proteínas,{" "}
                  {((caloriasCarbs / totalCaloriasMacros) * 100).toFixed(0)}% de
                  carboidratos e{" "}
                  {((caloriasGorduras / totalCaloriasMacros) * 100).toFixed(0)}%
                  de gorduras
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 mt-0.5">
                  •
                </span>
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  <strong>Média diária:</strong> Consumo médio de{" "}
                  {avgDailyCalories.toFixed(0)} kcal/dia em{" "}
                  {avgDailyMeals.toFixed(1)} refeições
                </p>
              </div>
              {nutritionSummary.avg_fibra_g < 15 && (
                <div className="flex items-start gap-2">
                  <span className="text-orange-600 dark:text-orange-400 mt-0.5">
                    ⚠
                  </span>
                  <p className="text-xs sm:text-sm text-orange-700 dark:text-orange-300">
                    <strong>Atenção:</strong> O consumo de fibras está abaixo do
                    recomendado. Considere aumentar a ingestão de frutas,
                    vegetais e grãos integrais
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h5 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
            Análise Nutricional (TACO)
          </h5>
          <p className="mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            {nutritionSummary.meals_with_taco_data} de {totalMeals} refeições
            com dados nutricionais completos ({tacoDataPercentage}%)
          </p>
        </div>
        <div className={`rounded-lg px-3 py-1.5 ${nutritionQuality.bgColor}`}>
          <span
            className={`text-xs sm:text-sm font-semibold ${nutritionQuality.color}`}
          >
            {nutritionQuality.message}
          </span>
        </div>
      </div>

      {/* Grid de Estatísticas Gerais */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          icon={
            <FiCalendar className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
          }
          iconBgColor="bg-blue-100 dark:bg-blue-900/30"
          title="Período Analisado"
          value={`${daysWithMeals} dias`}
          subtitle={`de ${daysAnalyzed} dias disponíveis`}
        />

        <MetricCard
          icon={
            <FiCheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
          }
          iconBgColor="bg-green-100 dark:bg-green-900/30"
          title="Taxa de Aderência"
          value={`${adherenceRate.toFixed(0)}%`}
          subtitle="dias com registro de refeições"
        />

        <MetricCard
          icon={
            <FiZap className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" />
          }
          iconBgColor="bg-purple-100 dark:bg-purple-900/30"
          title="Calorias Médias/Dia"
          value={`${avgDailyCalories.toFixed(0)} kcal`}
          subtitle={`${(avgDailyCalories / avgDailyMeals).toFixed(0)} kcal/refeição`}
        />

        <MetricCard
          icon={
            <FiTrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600 dark:text-indigo-400" />
          }
          iconBgColor="bg-indigo-100 dark:bg-indigo-900/30"
          title="Refeições/Dia"
          value={avgDailyMeals.toFixed(1)}
          subtitle={`${totalMeals} refeições no total`}
        />
      </div>

      {/* Grid de Macronutrientes */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          icon={
            <FiActivity className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 dark:text-red-400" />
          }
          iconBgColor="bg-red-100 dark:bg-red-900/30"
          title="Proteínas"
          value={`${nutritionSummary.total_proteina_g.toFixed(1)}g`}
          subtitle={`${nutritionSummary.avg_proteina_g.toFixed(1)}g por dia • ${nutritionSummary.distribuicao_macros.proteina_pct.toFixed(0)}%`}
        />

        <MetricCard
          icon={
            <FiZap className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600 dark:text-orange-400" />
          }
          iconBgColor="bg-orange-100 dark:bg-orange-900/30"
          title="Carboidratos"
          value={`${nutritionSummary.total_carboidrato_g.toFixed(1)}g`}
          subtitle={`${nutritionSummary.avg_carboidrato_g.toFixed(1)}g por dia • ${nutritionSummary.distribuicao_macros.carboidrato_pct.toFixed(0)}%`}
        />

        <MetricCard
          icon={
            <FiDroplet className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 dark:text-yellow-400" />
          }
          iconBgColor="bg-yellow-100 dark:bg-yellow-900/30"
          title="Gorduras"
          value={`${nutritionSummary.total_lipideos_g.toFixed(1)}g`}
          subtitle={`${nutritionSummary.avg_lipideos_g.toFixed(1)}g por dia • ${nutritionSummary.distribuicao_macros.lipideos_pct.toFixed(0)}%`}
        />

        <MetricCard
          icon={
            <FiTarget className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
          }
          iconBgColor="bg-green-100 dark:bg-green-900/30"
          title="Fibras"
          value={`${nutritionSummary.total_fibra_g.toFixed(1)}g`}
          subtitle={`${nutritionSummary.avg_fibra_g.toFixed(1)}g por dia`}
        />
      </div>

      {/* Gráfico de Distribuição de Macros */}
      <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-4 sm:p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
        <h5 className="mb-3 sm:mb-4 text-base sm:text-lg font-bold text-gray-900 dark:text-white">
          Distribuição de Macronutrientes
        </h5>
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          {/* Barra de Progresso */}
          <div className="flex-1 w-full">
            <div className="h-8 sm:h-10 w-full rounded-lg overflow-hidden flex shadow-inner">
              <div
                className="flex items-center justify-center bg-red-500 text-white text-xs sm:text-sm font-bold transition-all"
                style={{
                  width: `${nutritionSummary.distribuicao_macros.proteina_pct}%`,
                }}
              >
                {nutritionSummary.distribuicao_macros.proteina_pct.toFixed(0)}%
              </div>
              <div
                className="flex items-center justify-center bg-orange-500 text-white text-xs sm:text-sm font-bold transition-all"
                style={{
                  width: `${nutritionSummary.distribuicao_macros.carboidrato_pct}%`,
                }}
              >
                {nutritionSummary.distribuicao_macros.carboidrato_pct.toFixed(
                  0,
                )}
                %
              </div>
              <div
                className="flex items-center justify-center bg-yellow-500 text-white text-xs sm:text-sm font-bold transition-all"
                style={{
                  width: `${nutritionSummary.distribuicao_macros.lipideos_pct}%`,
                }}
              >
                {nutritionSummary.distribuicao_macros.lipideos_pct.toFixed(0)}%
              </div>
            </div>
          </div>

          {/* Legenda */}
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-red-500"></div>
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Proteínas
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-orange-500"></div>
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Carboidratos
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Gorduras
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Detalhamento de Calorias por Macronutriente */}
      <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-4 sm:p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
        <h5 className="mb-3 sm:mb-4 text-base sm:text-lg font-bold text-gray-900 dark:text-white">
          Calorias por Macronutriente
        </h5>
        <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-3">
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm font-medium text-red-600 dark:text-red-400">
                Proteínas
              </span>
              <FiActivity className="h-4 w-4 text-red-600 dark:text-red-400" />
            </div>
            <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              {caloriasProteinas.toFixed(0)} kcal
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {nutritionSummary.total_proteina_g.toFixed(1)}g × 4 kcal/g
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {((caloriasProteinas / totalCaloriasMacros) * 100).toFixed(0)}%
              das calorias
            </p>
          </div>

          <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm font-medium text-orange-600 dark:text-orange-400">
                Carboidratos
              </span>
              <FiZap className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            </div>
            <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              {caloriasCarbs.toFixed(0)} kcal
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {nutritionSummary.total_carboidrato_g.toFixed(1)}g × 4 kcal/g
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {((caloriasCarbs / totalCaloriasMacros) * 100).toFixed(0)}% das
              calorias
            </p>
          </div>

          <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm font-medium text-yellow-600 dark:text-yellow-400">
                Gorduras
              </span>
              <FiDroplet className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            </div>
            <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              {caloriasGorduras.toFixed(0)} kcal
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {nutritionSummary.total_lipideos_g.toFixed(1)}g × 9 kcal/g
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {((caloriasGorduras / totalCaloriasMacros) * 100).toFixed(0)}% das
              calorias
            </p>
          </div>
        </div>

        {/* Informação adicional sobre Fibras */}
        <div className="mt-4 rounded-lg bg-green-50 dark:bg-green-900/20 p-3 sm:p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 mb-2">
            <FiTarget className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400">
              Consumo de Fibras
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              {nutritionSummary.total_fibra_g.toFixed(1)}g totais
            </p>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              • {nutritionSummary.avg_fibra_g.toFixed(1)}g/dia
            </span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
            {fiberStatus}
          </p>
        </div>
      </div>
    </>
  );
}
